import React, { useState, useRef, useEffect } from "react";
import HorizontalBar from "@assets/images/HorizontalBar";
import HsCode from "@assets/images/HsCode";
import Trends from "./Trends";
import TwoAnalyticsWidget from "./TwoAnalyticsWidget";
import VerticalBar from "./VerticalBar";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";

const AnalyticsSideDawer = ({ analyticsFieldKeys, setShowFindWidget, showFindWidget, analyticsCountryID, pathname, currentUserPlan }) => {
  const [drawerPosition, setDrawerPosition] = useState({
    position: "absolute",
    top: "0px",
  });
  const drawerRef = useRef(null);
  const widgetRefs = useRef({});

  const scrollToWidget = (key) => {
    trademoTrackingEvents(trackingEventDictionary.analyticsTabFindWidget, generateTrackingObject(analyticsCountryID, pathname, currentUserPlan));
    if (widgetRefs.current[key]) {
      const element = widgetRefs.current[key];
      const offset = 70;

      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // Handle drawer positioning on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      // Determine positioning based on scroll position
      if (scrollTop > 215) {
        setDrawerPosition({
          position: "fixed",
          top: "50px",
          right: "20px",
        });
      } else {
        setDrawerPosition({
          position: "absolute",
          top: "0px",
          right: "0px",
        });
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Initial positioning
    handleScroll();

    // Cleanup event listener
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Handle outside click to close drawer
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target) && showFindWidget) {
        setShowFindWidget(true);
      }
    };

    if (showFindWidget) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFindWidget]);

  useEffect(() => {
    analyticsFieldKeys.forEach((item) => {
      const element = document.getElementById(item?.key);
      if (element) {
        widgetRefs.current[item?.key] = element;
      }
    });
  }, [analyticsFieldKeys]);

  return (
    <>
      {
        <div
          ref={drawerRef}
          style={{
            position: drawerPosition.position,
            top: `${drawerPosition.top}`,
            right: `${drawerPosition.right}`,
            height: "calc(100vh - 0px)",
          }}
          className={`tw-right-0 tw-w-[302px] tw-text-left tw-bg-white tw-shadow-lg tw-transform tw-transition-transform tw-duration-300 tw-ease-in-out tw-z-50 
          `}>
          <div className="tw-p-4 tw-pb-16 tw-space-y-2 tw-overflow-y-auto tw-bg-white tw-border tw-border-gray-200 tw-h-full">
            <div
              className="tw-absolute tw-flex tw-items-center tw-justify-center tw-h-5 tw-w-5 tw-left-[-8px] tw-bg-white tw-top-[-8px] tw-cursor-pointer tw-border tw-rounded-3xl tw-border-gray-300"
              onClick={() => {
                setShowFindWidget(true);
              }}>
              <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
                <path d="M291-253.85 253.85-291l189-189-189-189L291-706.15l189 189 189-189L706.15-669l-189 189 189 189L669-253.85l-189-189-189 189Z" />
              </svg>
            </div>
            <div className="tw-flex tw-align-top tw-gap-1 tw-text-xs">
              <span>
                <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#53b1fd">
                  <path d="M444-288h72v-240h-72v240Zm35.79-312q15.21 0 25.71-10.29t10.5-25.5q0-15.21-10.29-25.71t-25.5-10.5q-15.21 0-25.71 10.29t-10.5 25.5q0 15.21 10.29 25.71t25.5 10.5Zm.49 504Q401-96 331-126t-122.5-82.5Q156-261 126-330.96t-30-149.5Q96-560 126-629.5q30-69.5 82.5-122T330.96-834q69.96-30 149.5-30t149.04 30q69.5 30 122 82.5T834-629.28q30 69.73 30 149Q864-401 834-331t-82.5 122.5Q699-156 629.28-126q-69.73 30-149 30Zm-.28-72q130 0 221-91t91-221q0-130-91-221t-221-91q-130 0-221 91t-91 221q0 130 91 221t221 91Zm0-312Z" />
                </svg>
              </span>
              <span> Go straight to the widget you need to find with a single click </span>
            </div>
            {analyticsFieldKeys && analyticsFieldKeys.length > 0 ? (
              <div className="tw-flex tw-flex-col tw-gap-y-4">
                {analyticsFieldKeys.map(
                  (item, index) =>
                    item?.label && (
                      <span key={item?.key || index} className="tw-cursor-pointer hover:tw-bg-gray-100 tw-py-2 tw-px-3 tw-border !tw-rounded-lg tw-bg-white tw-transition-colors" onClick={() => scrollToWidget(item?.key)}>
                        <div className="tw-flex tw-gap-x-1">
                          <div className="tw-w-5 tw-h-5">{item?.type === "trends" ? <Trends /> : item?.type === "horizontalCountry" || item?.type === "verticalBar" ? <VerticalBar /> : item?.type === "horizontalBar" ? <HorizontalBar /> : item?.type === "hsCode" ? <HsCode /> : <TwoAnalyticsWidget />}</div>
                          <span className="tw-text-sm tw-font-semibold tw-text-gray-800">{item?.label}</span>
                        </div>
                      </span>
                    )
                )}
              </div>
            ) : (
              <p className="tw-text-gray-500 tw-text-center">No widgets available</p>
            )}
          </div>
        </div>
      }
    </>
  );
};

export default React.memo(AnalyticsSideDawer);
